/* Simulations℠ /testify — capture flow JS.
 * Wires the mic + Whisper transcription + form submit + state transitions
 * against the sim-testify Worker at simulations.so/api/testify/*.
 *
 * Loaded via <script> from sim-testify.liquid. Vanilla JS, no deps.
 * iOS Safari tested for MediaRecorder microphone permission flow.
 *
 * Author: Claude / KM 2026-05-18
 */
(function () {
  'use strict';

  // Hardcoded www to bypass apex→www 301 redirect (which converts POST → GET → 404).
  // Worker CORS already permits both simulations.so and www.simulations.so origins.
  const API_BASE = 'https://www.simulations.so/api/testify';
  const shell = document.querySelector('.t-shell');
  if (!shell) return;

  // ── Internal state ──────────────────────────────────────────────────────
  let mediaRecorder = null;
  let audioChunks = [];
  let recordingTimerInterval = null;
  let recordingSeconds = 0;
  let transcriptionResult = null; // { transcript, audio_url, audio_key }

  function setState(state) {
    const allowed = ['default','no-sim','recording','transcribing','review','type','submitting','success','error'];
    if (!allowed.includes(state)) state = 'default';
    shell.setAttribute('data-t-current', state);
  }

  function getCurrentState() {
    return shell.getAttribute('data-t-current') || 'default';
  }

  // ── Helpers ─────────────────────────────────────────────────────────────
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  function getActivePanel() {
    return $(`[data-t-state="${getCurrentState()}"]`);
  }

  function startTimer() {
    recordingSeconds = 0;
    updateTimerDisplay();
    recordingTimerInterval = setInterval(() => {
      recordingSeconds++;
      updateTimerDisplay();
    }, 1000);
  }

  function stopTimer() {
    clearInterval(recordingTimerInterval);
    recordingTimerInterval = null;
  }

  function updateTimerDisplay() {
    const m = Math.floor(recordingSeconds / 60);
    const s = String(recordingSeconds % 60).padStart(2, '0');
    $$('[data-t-state="recording"] .t-timer').forEach((el) => {
      el.textContent = `${m}:${s}`;
    });
  }

  function getSupportedMimeType() {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
    for (const t of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) return t;
    }
    return '';
  }

  // ── Mic record / stop ───────────────────────────────────────────────────
  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();
      mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunks = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunks.push(e.data); };
      mediaRecorder.onstop = handleRecordingStop;
      mediaRecorder.start(250);
      setState('recording');
      startTimer();
    } catch (err) {
      console.error('Mic access error:', err);
      setState('error');
    }
  }

  function stopRecording() {
    if (!mediaRecorder || mediaRecorder.state === 'inactive') return;
    try {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((t) => t.stop());
    } catch (err) {
      console.error('Stop recording error:', err);
    }
    stopTimer();
  }

  async function handleRecordingStop() {
    const mimeType = mediaRecorder?.mimeType || 'audio/webm';
    const blob = new Blob(audioChunks, { type: mimeType });

    setState('transcribing');

    try {
      const form = new FormData();
      form.append('audio', blob, 'testimony.webm');

      const res = await fetch(`${API_BASE}/transcribe`, {
        method: 'POST',
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Transcription failed');

      transcriptionResult = data;

      // Pre-fill review state textarea
      const reviewTextarea = $('[data-t-state="review"] .t-textarea');
      if (reviewTextarea) reviewTextarea.value = data.transcript || '';

      setState('review');
    } catch (err) {
      console.error('Transcribe error:', err);
      // Fall back to type-instead — user can still submit text
      setState('type');
    }
  }

  // ── Submit ──────────────────────────────────────────────────────────────
  async function handleSubmit(panelState) {
    const panel = $(`[data-t-state="${panelState}"]`);
    if (!panel) return;

    const testimony = panel.querySelector('.t-textarea')?.value?.trim() || '';
    const inputs = panel.querySelectorAll('input');
    let name = '', occupation = '', city = '', company = '';
    inputs.forEach((input) => {
      const placeholder = (input.placeholder || '').toLowerCase();
      if (placeholder.includes('kristian')) name = input.value.trim();
      else if (placeholder.includes('founder')) occupation = input.value.trim();
      else if (placeholder.includes('melbourne')) city = input.value.trim();
      else if (placeholder.includes('other internet')) company = input.value.trim();
    });

    // Validate locally before posting
    if (!testimony || testimony.length < 10) {
      alert('Testimony is too short. Add a few more words.');
      return;
    }
    if (!name) {
      alert('Name is required.');
      return;
    }

    setState('submitting');

    const simulation = readSelectedSim() || '001 · Pastlife';

    const payload = {
      simulation,
      name,
      occupation: occupation || null,
      city: city || null,
      company: company || null,
      testimony,
      audio_url: transcriptionResult?.audio_url || null,
      audio_key: transcriptionResult?.audio_key || null,
    };

    try {
      const res = await fetch(`${API_BASE}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setState('success');
    } catch (err) {
      console.error('Submit error:', err);
      // Keep them on review with the data preserved; show alert.
      alert('Could not save testimony: ' + err.message);
      setState(panelState);
    }
  }

  function readSelectedSim() {
    // Pulls the currently displayed sim from the active select OR meta value
    const activePanel = getActivePanel();
    if (!activePanel) return null;
    const selectBtn = activePanel.querySelector('.t-select__btn span');
    if (selectBtn && selectBtn.textContent && !/which simulation/i.test(selectBtn.textContent)) {
      return selectBtn.textContent.trim();
    }
    const metaValue = activePanel.querySelector('.t-meta__value');
    if (metaValue) return metaValue.textContent.trim();
    return null;
  }

  // ── Sim dropdown toggle (basic) ────────────────────────────────────────
  function toggleSimSelect(panel) {
    const sel = panel.querySelector('.t-select');
    if (!sel) return;
    sel.classList.toggle('t-select--open');
    const isOpen = sel.classList.contains('t-select--open');
    // Build / show menu if not present
    let menu = sel.querySelector('.t-select__menu');
    if (isOpen && !menu) {
      menu = document.createElement('ul');
      menu.className = 't-select__menu';
      menu.setAttribute('role', 'listbox');
      menu.innerHTML = `
        <li data-sim="Simulations">Simulations<sup>℠</sup> <span class="t-select__hint">studio</span></li>
        <li data-sim="001 · Pastlife">001 · Pastlife</li>
      `;
      sel.appendChild(menu);
      menu.querySelectorAll('li').forEach((li) => {
        li.addEventListener('click', () => {
          const val = li.getAttribute('data-sim');
          const btnSpan = sel.querySelector('.t-select__btn span');
          if (btnSpan) btnSpan.textContent = val;
          sel.classList.remove('t-select--open');
          menu.remove();
        });
      });
    } else if (!isOpen && menu) {
      menu.remove();
    }
  }

  // ── Wire interactions ──────────────────────────────────────────────────
  function wire() {
    // Mic button (in default, no-sim, recording, mobile)
    $$('.t-mic').forEach((mic) => {
      mic.addEventListener('click', () => {
        const state = getCurrentState();
        if (state === 'recording') {
          stopRecording();
        } else if (state === 'no-sim') {
          // No sim selected; do nothing (UI is dimmed anyway, but make sure)
          return;
        } else {
          startRecording();
        }
      });
    });

    // "or type instead" textlinks
    $$('.t-textlink').forEach((link) => {
      const isBack = link.classList.contains('t-textlink--back');
      link.addEventListener('click', () => {
        if (isBack) {
          setState('default');
        } else {
          setState('type');
        }
      });
    });

    // Submit buttons (in review, type, error states)
    $$('.btn.btn--solid').forEach((btn) => {
      const panel = btn.closest('[data-t-state]');
      if (!panel) return;
      const state = panel.getAttribute('data-t-state');
      if (['review', 'type', 'error'].includes(state)) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          handleSubmit(state);
        });
      }
    });

    // Sim dropdown toggles
    $$('.t-select__btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const panel = btn.closest('[data-t-state]');
        if (panel) toggleSimSelect(panel);
      });
    });

    // Click anywhere else closes any open dropdowns
    document.addEventListener('click', () => {
      $$('.t-select--open').forEach((s) => {
        s.classList.remove('t-select--open');
        const m = s.querySelector('.t-select__menu');
        if (m) m.remove();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
})();